import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaUsers,
  FaEnvelope,
  FaCheck,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { useOrganization } from "../../context/OrganizationContext";
import { useAuth } from "../../context/AuthContext";
import { getOrganizationFirstSetup } from "../../utility/organizationFirstSetup";
import OrganizationFirstSetup from "./OrganizationFirstSetup";

const OrganizationLanding = () => {
  const navigate = useNavigate();
  const { refreshOrganizations } = useOrganization();
  const { user } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [processingInvites, setProcessingInvites] = useState(new Set());
  const [setupStep, setSetupStep] = useState(
    () => getOrganizationFirstSetup()?.step || 1
  );

  useEffect(() => {
    if (user?.email) {
      fetchPendingInvites();
    }
  }, [user?.email]);

  const fetchPendingInvites = async () => {
    if (!user?.email) {
      return;
    }

    setInvitesLoading(true);

    try {
      const { data: invites, error } = await supabase.rpc(
        "get_pending_invites_by_email"
      );

      if (error) {
        console.error("RPC function error:", error);

        const { data: directInvites, error: directError } = await supabase
          .from("organizationInvites")
          .select(
            `
            id,
            email,
            status,
            "createdAt",
            "expiredAt",
            "organizationID",
            organizations (
              id,
              name,
              "nameAbbreviation",
              description
            )
          `
          )
          .eq("email", user.email)
          .eq("status", "pending")
          .gt("expiredAt", new Date().toISOString())
          .order("createdAt", { ascending: false });

        if (directError) {
          console.error("Direct query error:", directError);
          return;
        }

        setPendingInvites(directInvites || []);
        return;
      }

      const transformedInvites = (invites || []).map((invite) => ({
        id: invite.id,
        email: invite.email,
        status: invite.status,
        createdAt: invite.createdAt,
        expiredAt: invite.expiredAt,
        organizationID: invite.organizationID,
        organizations: {
          id: invite.organizationID,
          name: invite.organization_name,
          nameAbbreviation: invite.organization_abbreviation,
          description: invite.organization_description,
        },
      }));

      setPendingInvites(transformedInvites);
    } catch (error) {
      console.error("Error fetching pending invites:", error);
    } finally {
      setInvitesLoading(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    if (!user) return;

    setProcessingInvites((prev) => new Set([...prev, inviteId]));
    try {
      const { data: result, error } = await supabase.rpc(
        "accept_organization_invite",
        {
          invite_id: inviteId,
        }
      );

      if (error) {
        console.error("Error accepting invite:", error);
        setErrorMessage("Failed to accept invitation. Please try again.");
        return;
      }

      if (result && result.length > 0 && result[0].success) {
        await refreshOrganizations();
        navigate(`/organization/${result[0].organization_id}`);
      } else {
        setErrorMessage("Failed to accept invitation. Please try again.");
      }
    } catch (error) {
      console.error("Error accepting invite:", error);
      setErrorMessage("Failed to accept invitation. Please try again.");
    } finally {
      setProcessingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(inviteId);
        return newSet;
      });
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    setProcessingInvites((prev) => new Set([...prev, inviteId]));
    try {
      const { error } = await supabase
        .from("organizationInvites")
        .update({ status: "declined" })
        .eq("id", inviteId);

      if (error) {
        console.error("Error declining invite:", error);
        setErrorMessage("Failed to decline invitation. Please try again.");
        return;
      }

      setPendingInvites((prev) =>
        prev.filter((invite) => invite.id !== inviteId)
      );
    } catch (error) {
      console.error("Error declining invite:", error);
      setErrorMessage("Failed to decline invitation. Please try again.");
    } finally {
      setProcessingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(inviteId);
        return newSet;
      });
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="mx-auto h-24 w-24 bg-brand-charcoal rounded-lg flex items-center justify-center mb-6">
            <FaBuilding className="h-12 w-12 text-brand-cream" />
          </div>
          <h1 className="font-display font-bold text-3xl text-brand-charcoal tracking-tight mb-4">
            Get started
          </h1>
          <p className="text-lg text-brand-muted max-w-2xl mx-auto">
            Create your organization, add the house you bowl at, then create
            your first tournament.
          </p>
        </div>

        {errorMessage ? (
          <div className="mb-6">
            <InlineAlert
              type="error"
              title="There was a problem"
              message={errorMessage}
              onClose={() => setErrorMessage("")}
            />
          </div>
        ) : null}

        {pendingInvites.length > 0 && (
          <div className="mb-8">
            <div className="card-board p-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-brand-cream rounded-lg flex items-center justify-center mr-3">
                  <FaEnvelope className="h-5 w-5 text-brand-muted" />
                </div>
                <div>
                  <h2 className="heading-display text-xl">
                    Pending Invitations
                  </h2>
                  <p className="text-sm text-brand-muted">
                    You have {pendingInvites.length} pending invitation
                    {pendingInvites.length !== 1 ? "s" : ""} to join
                    organizations
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="border border-brand-charcoal/10 rounded-lg p-4 hover:bg-brand-cream/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="text-lg font-medium text-brand-charcoal">
                            {invite.organizations?.name}
                          </h3>
                          {invite.organizations?.nameAbbreviation && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-brand-charcoal text-brand-cream rounded-lg">
                              {invite.organizations.nameAbbreviation}
                            </span>
                          )}
                        </div>
                        {invite.organizations?.description && (
                          <p className="text-sm text-brand-muted mt-1">
                            {invite.organizations.description}
                          </p>
                        )}
                        <p className="text-xs text-brand-mist mt-2">
                          Invited on{" "}
                          {new Date(invite.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          type="button"
                          onClick={() => handleAcceptInvite(invite.id)}
                          disabled={processingInvites.has(invite.id)}
                          className="bg-brand-gold text-brand-charcoal px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide hover:bg-brand-gold/90 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {processingInvites.has(invite.id) ? (
                            <>
                              <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                              Accepting...
                            </>
                          ) : (
                            <>
                              <FaCheck className="mr-2 h-4 w-4" />
                              Accept
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeclineInvite(invite.id)}
                          disabled={processingInvites.has(invite.id)}
                          className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {processingInvites.has(invite.id) ? (
                            <>
                              <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                              Declining...
                            </>
                          ) : (
                            <>
                              <FaTimes className="mr-2 h-4 w-4" />
                              Decline
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {invitesLoading && (
          <div className="mb-8">
            <div className="card-board p-6">
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
                <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
              </div>
            </div>
          </div>
        )}

        <OrganizationFirstSetup onStepChange={setSetupStep} />

        {setupStep === 1 ? (
          <div className="card-board p-8 mt-8">
            <div className="flex items-center mb-4">
              <div className="h-12 w-12 bg-brand-cream rounded-lg flex items-center justify-center mr-4">
                <FaUsers className="h-6 w-6 text-brand-muted" />
              </div>
              <div>
                <h2 className="heading-display text-xl">
                  Already have an invitation?
                </h2>
                <p className="text-sm text-brand-muted">
                  Accept it above if one is waiting. Otherwise ask the owner to
                  send one.
                </p>
              </div>
            </div>
            <p className="text-sm text-brand-muted">
              Starting a new group? Fill in the form above.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrganizationLanding;
